import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import dayjs from "dayjs";
import { http } from "../../utils/httpCommon";
import { useApiUrls } from "../useApiUrl";
import {
  Route,
  allRouteTableSchema,
} from "../../services/prePlanningService.types";
import { MergeRoutesRequest } from "../../services/planningService.types";
import { useDateSearchParamOrFallbackToToday } from "../useDateSearchParamOrFallbackToToday";
import { dateToInt } from "../../utils/dateTimeHelper";
import { toast } from "../../utils/snackbarHelper";
import { toastMessage } from "../../constants/strings";
import { useSelectedServiceCenter } from "../useSelectedServiceCenter";
import { PlanTableFormat } from "../../types/planning/plan.type";
import { safeParsePartial } from "../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import {
  CreateRouteRequest,
  LockRoute,
  MassageRoute,
} from "./useDeliveryRoutes.types";
import { plannedRoutesQueryKey } from "./usePlannedRoutes";

export const deliveryRoutesKeys = {
  deliveryRoutes: (sicId: number, date: number) =>
    ["delivery-routes", sicId, date] as const,
  deliveryRoutesSummary: (
    sicId: number,
    date: number,
    searchTerm: string | undefined = undefined
  ) => {
    let queryKey: unknown[] = ["delivery-routes-summary", date, sicId];

    if (searchTerm !== undefined) {
      queryKey = [...queryKey, searchTerm];
    }

    return queryKey;
  },
  allRoutes: (sicId: number, date: number, searchTerm?: string) =>
    deliveryRoutesKeys.deliveryRoutesSummary(sicId, date, searchTerm),
};

export type MergeRoutesMutation = {
  routeId: number;
  request: MergeRoutesRequest;
};

export const useMergeRoutes = (
  successHandler: (
    data: number,
    mergeRoutesMutation: MergeRoutesMutation
  ) => void
) => {
  const queryClient = useQueryClient();
  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const hookUrls = useApiUrls();
  const { mergeRoutes } = hookUrls;

  const mergeRoutesApi = async (mergeRoutesMutation: MergeRoutesMutation) => {
    const { status } = await http.patch(
      mergeRoutes(mergeRoutesMutation.routeId),
      mergeRoutesMutation.request
    );
    return status;
  };

  return useMutation({
    mutationFn: async (mergeRoutesMutation: MergeRoutesMutation) =>
      await mergeRoutesApi(mergeRoutesMutation),
    onSuccess: successHandler,
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: deliveryRoutesKeys.deliveryRoutesSummary(
          serviceCenter.id,
          dateToInt(selectedDate)
        ),
      });
      queryClient.invalidateQueries({
        queryKey: plannedRoutesQueryKey(
          dateToInt(selectedDate),
          serviceCenter.id
        ),
      });
    },
    onError: () => toast(toastMessage.generics.error, { variant: "error" }),
  });
};

type UseCreateRouteArgs = {
  createRouteRequest: CreateRouteRequest;
  onSuccess: (shipmentsCount: number, routeName: string) => void;
  onError?: (error: unknown) => void;
};

export const useCreateRoute = ({
  createRouteRequest,
  onSuccess,
  onError,
}: UseCreateRouteArgs) => {
  const hookUrls = useApiUrls();
  const { createRoute } = hookUrls;

  const { sicId, unixTime } = createRouteRequest;
  const [serviceCenter] = useSelectedServiceCenter();
  const invalidateQueryRoutes = useInvalidateQueryRoutes();

  return useMutation({
    mutationFn: () =>
      http.post(createRoute(createRouteRequest.unixTime, sicId), {
        ...createRouteRequest,
        startTimeTimestampUTC: dayjs(createRouteRequest.startTimeTimestampUTC, {
          utc: false,
        })
          .tz(serviceCenter.timeZone, true)
          .toISOString(),
        sicId: serviceCenter.id,
        doorId: createRouteRequest.doorId || null,
        tractorEquipmentId: createRouteRequest.tractorEquipmentId,
        trailerEquipmentId: createRouteRequest.trailerEquipmentId,
        straightTruckEquipmentId: createRouteRequest.straightTruckEquipmentId,
        driverId: createRouteRequest.driverId || "0",
        assignments: createRouteRequest.assignments || [],
      }),
    onSuccess: () => {
      const selectedShipments = createRouteRequest.assignments?.flatMap(
        (assignment) => assignment.shipmentIds.length
      );

      const numSelectedShipments = selectedShipments?.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0
      );
      onSuccess(numSelectedShipments ?? 0, createRouteRequest.name);
    },
    onError,
    onSettled: () => {
      const allShipmentIds =
        createRouteRequest.assignments?.flatMap(
          (assignment) => assignment.shipmentIds
        ) ?? [];

      invalidateQueryRoutes(unixTime, sicId, {
        includeUnassignedShipments: true,
        includeShipmentsSearch: allShipmentIds.length > 0,
      });
    },
  });
};

export const useCreatePickupOnlyRoute = ({
  createRouteRequest,
  onSuccess,
  onError,
}: UseCreateRouteArgs) => {
  const hookUrls = useApiUrls();
  const { createPickupOnlyRoute } = hookUrls;

  const { sicId, unixTime } = createRouteRequest;
  const [serviceCenter] = useSelectedServiceCenter();
  const invalidateQueryRoutes = useInvalidateQueryRoutes();

  return useMutation({
    mutationFn: () =>
      http.post(createPickupOnlyRoute(createRouteRequest.unixTime, sicId), {
        ...createRouteRequest,
        startTimeTimestampUTC: dayjs(createRouteRequest.startTimeTimestampUTC, {
          utc: false,
        })
          .tz(serviceCenter.timeZone, true)
          .toISOString(),
        sicId: serviceCenter.id,
        doorId: null,
        tractorEquipmentId: createRouteRequest.tractorEquipmentId,
        trailerEquipmentId: createRouteRequest.trailerEquipmentId,
        straightTruckEquipmentId: createRouteRequest.straightTruckEquipmentId,
        driverId: createRouteRequest.driverId || "0",
        assignments: [],
      }),
    onSuccess: () => {
      const selectedShipments = createRouteRequest.assignments?.flatMap(
        (assignment) => assignment.shipmentIds.length
      );

      const numSelectedShipments = selectedShipments?.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0
      );
      onSuccess(numSelectedShipments ?? 0, createRouteRequest.name);
    },
    onError,
    onSettled: () => {
      const allShipmentIds =
        createRouteRequest.assignments?.flatMap(
          (assignment) => assignment.shipmentIds
        ) ?? [];

      invalidateQueryRoutes(unixTime, sicId, {
        includeUnassignedShipments: true,
        includeShipmentsSearch: allShipmentIds.length > 0,
      });
    },
  });
};

interface AllRoutesTableProps {
  date: number;
  sicId: number;
  searchTerm?: string;
}

export function useAllRoutesTable({
  date,
  sicId,
  searchTerm,
}: AllRoutesTableProps) {
  const allRoutesTableResponse = z.array(allRouteTableSchema);
  const hookUrls = useApiUrls();
  const { getDeliveryRoutesSummary } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  const getAllRouteData = async (
    sicId: number,
    date: number,
    searchTerm?: string
  ): Promise<Route[]> => {
    const { data, status } = await http.get(
      getDeliveryRoutesSummary(sicId, date),
      {
        params: { searchTerm },
      }
    );

    if (status === 204) return [];
    return isSafeParseEnabled
      ? safeParsePartial(allRoutesTableResponse, data, "useAllRoutesTable")
      : allRoutesTableResponse.parse(data);
  };

  return useQuery({
    queryKey: deliveryRoutesKeys.allRoutes(sicId, date, searchTerm),
    queryFn: () => getAllRouteData(sicId, date, searchTerm),
  });
}
interface MassageRouteProps {
  date: number;
  sicId: number;
  onSuccess: () => Promise<void> | void;
}

export const useMassageRoute = ({
  date,
  sicId,
  onSuccess,
}: MassageRouteProps) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { putMassageRoute } = hookUrls;

  return useMutation<
    null,
    Error,
    MassageRoute,
    { previousPlans?: PlanTableFormat[] }
  >({
    mutationFn: async ({ id, isPlanCompleted }: MassageRoute) => {
      await http.put(putMassageRoute(id), {
        isPlanCompleted,
      });
      return null;
    },
    onMutate: async (variables: MassageRoute) => {
      await queryClient.cancelQueries({
        queryKey: plannedRoutesQueryKey(date, sicId),
      });

      const previousPlans =
        queryClient.getQueryData<PlanTableFormat[]>(
          plannedRoutesQueryKey(date, sicId)
        ) ?? [];

      queryClient.setQueryData<PlanTableFormat[]>(
        plannedRoutesQueryKey(date, sicId),
        (oldPlans) => {
          if (!oldPlans) return oldPlans;
          return oldPlans.map((plan) =>
            plan.id === variables.id
              ? { ...plan, isPlanCompleted: variables.isPlanCompleted }
              : plan
          );
        }
      );

      return { previousPlans };
    },
    onError: (err, variables, context) => {
      if (context?.previousPlans) {
        queryClient.setQueryData(
          plannedRoutesQueryKey(date, sicId),
          context.previousPlans
        );
      }
      toast(toastMessage.shared.massageRoute.failed, {
        variant: "error",
      });
    },
    onSuccess,
  });
};
interface LockRouteProps {
  date: number;
  sicId: number;
  searchTerm?: string;
  onSuccess: () => Promise<void> | void;
}

export const useLockRoutes = ({ date, sicId, onSuccess }: LockRouteProps) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { lockRoutes } = hookUrls;

  return useMutation({
    mutationFn: async (routes: LockRoute[]) => {
      await http.post(lockRoutes, routes);
    },
    onSuccess: (_, lockedRoutes) => {
      queryClient.setQueryData<PlanTableFormat[]>(
        plannedRoutesQueryKey(date, sicId),
        (previousStateOfPlans) => {
          if (!previousStateOfPlans) return [];
          return previousStateOfPlans.map((plan) => {
            const match = lockedRoutes.find(
              (lockedRoute) => lockedRoute.id === plan.id
            );
            if (match) {
              return { ...plan, isCommitted: match.isLocked };
            }
            return plan;
          });
        }
      );
      onSuccess();
    },
    onError: () => toast(toastMessage.generics.error, { variant: "error" }),
  });
};

export const useOldLockRoutes = ({
  date,
  sicId,
  onSuccess,
}: LockRouteProps) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { lockRoutes } = hookUrls;

  return useMutation({
    mutationFn: async (routes: LockRoute[]) => {
      await http.post(lockRoutes, routes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: plannedRoutesQueryKey(date, sicId),
      });
      onSuccess();
    },
    onError: () => toast(toastMessage.generics.error, { variant: "error" }),
  });
};

export function useInvalidateQueryRoutes() {
  const queryClient = useQueryClient();
  const invalidateQueryRoutes = (
    date: number,
    sicId: number,
    options?: {
      includeUnassignedShipments?: boolean;
      includeShipmentsSearch?: boolean;
    }
  ) => {
    // Core route-related invalidations
    queryClient.invalidateQueries({
      queryKey: plannedRoutesQueryKey(date, sicId),
    });
    queryClient.invalidateQueries({
      queryKey: deliveryRoutesKeys.deliveryRoutesSummary(sicId, date),
    });
    queryClient.invalidateQueries({
      queryKey: deliveryRoutesKeys.deliveryRoutes(sicId, date),
    });

    // Optional shipment-related invalidations
    if (options?.includeUnassignedShipments) {
      queryClient.invalidateQueries({
        queryKey: ["unassigned", "shipments", date, sicId],
      });
    }

    if (options?.includeShipmentsSearch) {
      queryClient.invalidateQueries({
        queryKey: ["shipments", "search"],
      });
    }
  };
  return invalidateQueryRoutes;
}
